import { livFetch } from './client'
import type {
  Apartment,
  ApartmentFilterOptions,
  ApartmentFilterParams,
  ApartmentListResponse,
  ApartmentSource,
  IndexCategory,
  ListingType,
  PriceHistory
} from './types'

function appendBool(params: URLSearchParams, key: string, value?: boolean) {
  if (value) params.set(key, '1')
}

export function buildFilterParams(
  filters?: ApartmentFilterParams,
  listingType: ListingType = 'rent'
): URLSearchParams {
  const params = new URLSearchParams()
  params.set('listing_type', listingType)

  if (!filters) return params

  if (filters.minPrice !== undefined) {
    params.set('min_price', filters.minPrice.toString())
  }
  if (filters.maxPrice !== undefined) {
    params.set('max_price', filters.maxPrice.toString())
  }
  if (filters.minPricePerM2 !== undefined) {
    params.set('min_price_per_m2', filters.minPricePerM2.toString())
  }
  if (filters.maxPricePerM2 !== undefined) {
    params.set('max_price_per_m2', filters.maxPricePerM2.toString())
  }
  if (filters.minArea !== undefined) {
    params.set('min_area', filters.minArea.toString())
  }
  if (filters.maxArea !== undefined) {
    params.set('max_area', filters.maxArea.toString())
  }
  if (filters.roomCounts && filters.roomCounts.length > 0) {
    params.set('room_counts', [...filters.roomCounts].sort((a, b) => a - b).join(','))
  }
  if (filters.minRooms !== undefined) {
    params.set('min_rooms', filters.minRooms.toString())
  }
  if (filters.maxRooms !== undefined) {
    params.set('max_rooms', filters.maxRooms.toString())
  }
  if (filters.city) params.set('city', filters.city)
  if (filters.district) params.set('district', filters.district)
  appendBool(params, 'near_center', filters.nearCenter)
  if (filters.condition) params.set('condition', filters.condition)
  if (filters.buildingType) params.set('building_type', filters.buildingType)
  appendBool(params, 'has_parking', filters.hasParking)
  appendBool(params, 'has_balcony', filters.hasBalcony)
  appendBool(params, 'has_elevator', filters.hasElevator)
  appendBool(params, 'pets_allowed', filters.petsAllowed)
  appendBool(params, 'furnished', filters.furnished)
  appendBool(params, 'fully_furnished', filters.fullyFurnished)
  appendBool(params, 'available_now', filters.availableNow)
  if (filters.sortBy) params.set('sort_by', filters.sortBy)
  if (filters.rankBy) params.set('rank_by', filters.rankBy)
  if (filters.indexCategory) params.set('index_category', filters.indexCategory)
  if (filters.minIndex !== undefined) {
    params.set('min_index', filters.minIndex.toString())
  }
  if (filters.q?.trim()) params.set('q', filters.q.trim())
  if (filters.page !== undefined) params.set('page', filters.page.toString())
  if (filters.limit !== undefined) params.set('limit', filters.limit.toString())

  return params
}

export async function getApartments(
  filters?: ApartmentFilterParams,
  listingType: ListingType = 'rent',
  signal?: AbortSignal
): Promise<ApartmentListResponse> {
  const params = buildFilterParams(filters, listingType)
  return livFetch<ApartmentListResponse>(`/apartments?${params}`, { signal })
}

export async function getFilterOptions(
  listingType: ListingType = 'rent'
): Promise<ApartmentFilterOptions> {
  const params = new URLSearchParams({ listing_type: listingType })
  return livFetch<ApartmentFilterOptions>(`/apartments/filter-options?${params}`)
}

export interface MapBoundsRequest {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
  indexCategory?: IndexCategory
  filters?: ApartmentFilterParams
  listingType?: ListingType
  signal?: AbortSignal
}

export async function getApartmentsInBounds({
  minLng,
  minLat,
  maxLng,
  maxLat,
  indexCategory = 'general',
  filters,
  listingType = 'rent',
  signal
}: MapBoundsRequest): Promise<Apartment[]> {
  const params = buildFilterParams(filters, listingType)
  params.set('min_lng', minLng.toString())
  params.set('min_lat', minLat.toString())
  params.set('max_lng', maxLng.toString())
  params.set('max_lat', maxLat.toString())
  params.set('index_category', indexCategory)

  return livFetch<Apartment[]>(`/apartments/map/bounds?${params}`, { signal })
}

export async function getApartmentsByIds(
  ids: number[],
  indexCategory?: IndexCategory,
  signal?: AbortSignal
): Promise<Apartment[]> {
  if (ids.length === 0) {
    return []
  }

  const params = new URLSearchParams({ ids: ids.join(',') })
  if (indexCategory) {
    params.append('index_category', indexCategory)
  }

  return livFetch<Apartment[]>(`/apartments/by-ids?${params}`, { signal })
}

export async function getApartment(
  id: number,
  includeIndex = false,
  indexCategory?: IndexCategory
): Promise<Apartment> {
  const params = new URLSearchParams()
  if (includeIndex) {
    params.append('include_index', 'true')
  }
  if (indexCategory) {
    params.append('index_category', indexCategory)
  }

  const query = params.toString()
  const path = query ? `/apartments/${id}?${query}` : `/apartments/${id}`
  return livFetch<Apartment>(path)
}

export async function getPriceHistory(
  apartmentId: number,
  sourceWebsite?: string
): Promise<PriceHistory[]> {
  const params = new URLSearchParams()
  if (sourceWebsite) {
    params.append('source_website', sourceWebsite)
  }

  const query = params.toString()
  const path = query
    ? `/apartments/${apartmentId}/price-history?${query}`
    : `/apartments/${apartmentId}/price-history`
  return livFetch<PriceHistory[]>(path)
}

export async function getApartmentSources(
  apartmentId: number
): Promise<ApartmentSource[]> {
  return livFetch<ApartmentSource[]>(`/apartments/${apartmentId}/sources`)
}
